import { cva, type VariantProps } from "class-variance-authority";
import type { FC, SVGProps } from "react";
import { AiFillEdit, AiOutlineEdit } from "react-icons/ai";
import { BiBold, BiItalic, BiListOl, BiListUl } from "react-icons/bi";
import { FaRegSnowflake, FaSnowflake, FaUnderline, FaUserCheck } from "react-icons/fa";
import { GoShieldSlash, GoStop } from "react-icons/go";
import {
	HiDocumentText,
	HiDotsVertical,
	HiLocationMarker,
	HiMail,
	HiOutlineDocumentText,
	HiOutlineDotsVertical,
	HiOutlineLocationMarker,
	HiOutlineMail,
} from "react-icons/hi";
import {
	HiArchiveBox,
	HiArchiveBoxArrowDown,
	HiArrowDown,
	HiArrowDownTray,
	HiArrowPath,
	HiArrowRightEndOnRectangle,
	HiArrowSmallDown,
	HiArrowSmallUp,
	HiArrowsPointingIn,
	HiArrowsPointingOut,
	HiArrowTopRightOnSquare,
	HiArrowUp,
	HiArrowUpTray,
	HiAtSymbol,
	HiBolt,
	HiBookOpen,
	HiBuildingOffice,
	HiBuildingOffice2,
	HiCalendar,
	HiChatBubbleOvalLeft,
	HiCheck,
	HiCheckBadge,
	HiCheckCircle,
	HiChevronDoubleDown,
	HiChevronDoubleLeft,
	HiChevronDoubleRight,
	HiChevronDown,
	HiChevronLeft,
	HiChevronRight,
	HiChevronUp,
	HiChevronUpDown,
	HiClipboard,
	HiClock,
	HiDocument,
	HiDocumentArrowDown,
	HiDocumentChartBar,
	HiDocumentCheck,
	HiDocumentPlus,
	HiEllipsisHorizontal,
	HiEnvelope,
	HiExclamationCircle,
	HiExclamationTriangle,
	HiEye,
	HiEyeSlash,
	HiFolderOpen,
	HiFunnel,
	HiHandRaised,
	HiHandThumbDown,
	HiHandThumbUp,
	HiHashtag,
	HiHome,
	HiIdentification,
	HiInformationCircle,
	HiLink,
	HiLockClosed,
	HiLockOpen,
	HiMagnifyingGlass,
	HiMapPin,
	HiMiniLockClosed,
	HiMiniLockOpen,
	HiMinusCircle,
	HiOutlineArchiveBox,
	HiOutlineArchiveBoxArrowDown,
	HiOutlineArrowDown,
	HiOutlineArrowDownTray,
	HiOutlineArrowPath,
	HiOutlineArrowRightEndOnRectangle,
	HiOutlineArrowSmallDown,
	HiOutlineArrowSmallUp,
	HiOutlineArrowsPointingIn,
	HiOutlineArrowsPointingOut,
	HiOutlineArrowTopRightOnSquare,
	HiOutlineArrowUp,
	HiOutlineArrowUpTray,
	HiOutlineAtSymbol,
	HiOutlineBolt,
	HiOutlineBookOpen,
	HiOutlineBuildingOffice,
	HiOutlineBuildingOffice2,
	HiOutlineCalendar,
	HiOutlineChatBubbleOvalLeft,
	HiOutlineCheck,
	HiOutlineCheckBadge,
	HiOutlineCheckCircle,
	HiOutlineChevronDoubleDown,
	HiOutlineChevronDoubleLeft,
	HiOutlineChevronDoubleRight,
	HiOutlineChevronDown,
	HiOutlineChevronLeft,
	HiOutlineChevronRight,
	HiOutlineChevronUp,
	HiOutlineChevronUpDown,
	HiOutlineClipboard,
	HiOutlineClock,
	HiOutlineDocument,
	HiOutlineDocumentArrowDown,
	HiOutlineDocumentChartBar,
	HiOutlineDocumentCheck,
	HiOutlineDocumentPlus,
	HiOutlineEllipsisHorizontal,
	HiOutlineEnvelope,
	HiOutlineExclamationCircle,
	HiOutlineExclamationTriangle,
	HiOutlineEye,
	HiOutlineEyeSlash,
	HiOutlineFolderOpen,
	HiOutlineFunnel,
	HiOutlineHandRaised,
	HiOutlineHandThumbDown,
	HiOutlineHandThumbUp,
	HiOutlineHashtag,
	HiOutlineHome,
	HiOutlineIdentification,
	HiOutlineInformationCircle,
	HiOutlineLink,
	HiOutlineLockClosed,
	HiOutlineLockOpen,
	HiOutlineMagnifyingGlass,
	HiOutlineMapPin,
	HiOutlineMinusCircle,
	HiOutlinePaperAirplane,
	HiOutlinePencil,
	HiOutlinePencilSquare,
	HiOutlinePlay,
	HiOutlinePlayCircle,
	HiOutlinePlus,
	HiOutlinePlusCircle,
	HiOutlinePresentationChartLine,
	HiOutlineQueueList,
	HiOutlineScale,
	HiOutlineShieldCheck,
	HiOutlineSlash,
	HiOutlineSparkles,
	HiOutlineSquares2X2,
	HiOutlineStar,
	HiOutlineTableCells,
	HiOutlineTag,
	HiOutlineTrash,
	HiOutlineUser,
	HiOutlineUserGroup,
	HiOutlineUserPlus,
	HiOutlineUsers,
	HiOutlineViewColumns,
	HiOutlineWindow,
	HiOutlineXCircle,
	HiOutlineXMark,
	HiPaperAirplane,
	HiPencil,
	HiPencilSquare,
	HiPlay,
	HiPlayCircle,
	HiPlus,
	HiPlusCircle,
	HiPresentationChartLine,
	HiQueueList,
	HiScale,
	HiShieldCheck,
	HiSlash,
	HiSparkles,
	HiSquares2X2,
	HiStar,
	HiTableCells,
	HiTag,
	HiTrash,
	HiUser,
	HiUserGroup,
	HiUserPlus,
	HiUsers,
	HiViewColumns,
	HiWindow,
	HiXCircle,
	HiXMark,
} from "react-icons/hi2";
import type { IconType as ReactIconType } from "react-icons/lib";
import { PiHourglassBold, PiHourglassLow, PiHourglassLowFill } from "react-icons/pi";
import { RiDiamondRingFill, RiDiamondRingLine, RiFormula, RiGitMergeLine } from "react-icons/ri";
import { cn } from "../lib/utils";
import { trackingAttrs } from "../utils";

type CustomIconProps = SVGProps<SVGSVGElement> & { title?: string };

const HeadsetIcon = ({ className, title, ...props }: CustomIconProps) => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="100%"
		height="100%"
		viewBox="0 0 20 25"
		fill="currentColor"
		className={cn("overflow-visible opacity-[0.999]", className)}
		{...props}
	>
		{title ? <title>{title}</title> : null}
		<path
			fillRule="evenodd"
			clipRule="evenodd"
			d="M4.34378 4.34378C5.84448 2.84308 7.87986 2 10.0022 2C12.1245 2 14.1599 2.84308 15.6605 4.34378C17.1612 5.84448 18.0043 7.87986 18.0043 10.0022V11.0025H16.0036C15.2078 11.0025 14.4446 11.3186 13.8819 11.8813C13.3192 12.444 13.0031 13.2072 13.0031 14.003V17.0037C13.0031 17.7995 13.3192 18.5627 13.8819 19.1254C14.4446 19.6881 15.2078 20.0042 16.0036 20.0042H16.9435L16.2424 22.5453H11.0921C10.9598 22.148 10.5849 21.8616 10.1432 21.8616C9.59092 21.8616 9.14321 22.3093 9.14321 22.8616V22.8621V22.8626V22.8631V22.8636V22.864V22.8645V22.865V22.8655V22.8659V22.8664V22.8669V22.8673V22.8678V22.8682V22.8687V22.8692V22.8696V22.8701V22.8705V22.871V22.8714V22.8719V22.8723V22.8727V22.8732V22.8736V22.8741V22.8745V22.8749V22.8754V22.8758V22.8762V22.8766V22.8771V22.8775V22.8779V22.8783V22.8788V22.8792V22.8796V22.88V22.8804V22.8809V22.8813V22.8817V22.8821V22.8825V22.8829V22.8833V22.8838V22.8842V22.8846V22.885V22.8854V22.8858V22.8862V22.8866V22.887V22.8874V22.8878V22.8882V22.8886V22.889V22.8894V22.8898V22.8903V22.8907V22.8911V22.8915V22.8919V22.8923V22.8927V22.8931V22.8935V22.8939V22.8943V22.8947V22.8951V22.8955V22.8959V22.8963V22.8967V22.8971V22.8975V22.8979V22.8983V22.8987V22.8991V22.8995V22.8999V22.9004V22.9008V22.9012V22.9016V22.902V22.9024V22.9028V22.9032V22.9036V22.9041V22.9045V22.9049V22.9053V22.9057V22.9062V22.9066V22.907V22.9074V22.9078V22.9083V22.9087V22.9091V22.9096V22.91V22.9104V22.9109V22.9113V22.9117V22.9122V22.9126V22.9131V22.9135V22.9139V22.9144V22.9148V22.9153V22.9157V22.9162V22.9167V22.9171V22.9176V22.918V22.9185V22.919V22.9194V22.9199V22.9204V22.9209V22.9213V22.9218V22.9223V22.9228V22.9233V22.9238V22.9242V22.9247V22.9252V22.9257V22.9262V22.9267V22.9272V22.9277V22.9283V22.9288V22.9293V22.9298V22.9303V22.9309V22.9314V22.9319V22.9325V22.933V22.9335V22.9341V22.9346V22.9352V22.9357V22.9363V22.9368V22.9374V22.938V22.9385V22.9391V22.9397V22.9403V22.9408V22.9414V22.942V22.9426V22.9432V22.9438V22.9444V22.945V22.9456V22.9462V22.9469V22.9475V22.9481V22.9487V22.9494V22.95V22.9507V22.9513V22.952V22.9526V22.9533V22.9539V22.9546V22.9553V22.9559V22.9566V22.9573V22.958V22.9587V22.9594V22.9601V22.9608V22.9615V22.9622V22.9629V22.9637V22.9644V22.9651V22.9659V22.9666V22.9674V22.9681V22.9689V22.9696V22.9704V22.9712V22.9719V22.9727V22.9735V22.9743V22.9751V22.9759V22.9767V22.9775V22.9784V22.9792V22.98V22.9808V22.9817V22.9825V22.9834V22.9842V22.9851V22.986V22.9868V22.9877V22.9886V22.9895V22.9904V22.9913V22.9922V22.9931V22.994V22.9949V22.9959V22.9968V22.9978V22.9987V22.9997V23.0006V23.0016V23.0026V23.0035V23.0045V23.0055V23.0065V23.0075V23.0085V23.0095V23.0106V23.0116V23.0126V23.0137V23.0147V23.0158V23.0169V23.0179V23.019V23.0201V23.0212V23.0223V23.0234V23.0245V23.0256V23.0267V23.0279V23.029V23.0301V23.0313V23.0324V23.0336V23.0348V23.036V23.0372V23.0383V23.0396V23.0408V23.042V23.0432V23.0444V23.0457V23.0469V23.0482V23.0494V23.0507V23.052V23.0533V23.0546V23.0559V23.0572V23.0585V23.0598V23.0611V23.0625V23.0638V23.0652V23.0666V23.0679V23.0693V23.0707V23.0721V23.0735V23.0749V23.0763V23.0777V23.0792V23.0806V23.0821V23.0835V23.085V23.0865V23.088V23.0895V23.091V23.0925V23.094V23.0956V23.0971V23.0986V23.1002V23.1018V23.1033V23.1049V23.1065V23.1081V23.1097V23.1113V23.113V23.1146V23.1163V23.1179V23.1196V23.1212V23.1229V23.1246V23.1263V23.128V23.1298V23.1315V23.1332V23.135V23.1367V23.1385V23.1403V23.1421V23.1439V23.1457V23.1475V23.1493V23.1511V23.153V23.1548V23.1567V23.1586V23.1605V23.1624V23.1643V23.1662V23.1681V23.17V23.172V23.1739V23.1759V23.1779V23.1799V23.1819V23.1839V23.1859V23.1879V23.1899V23.192V23.1941V23.1961V23.1982V23.2003V23.2024V23.2045V23.2066V23.2088V23.2109V23.2131V23.2152V23.2174V23.2196V23.2218V23.224V23.2262V23.2285V23.2307V23.2329V23.2352V23.2375V23.2398V23.2421V23.2444V23.2467V23.249V23.2514V23.2537V23.2561V23.2585V23.2609V23.2633V23.2657V23.2681V23.2705V23.273V23.2754V23.2779V23.2804V23.2829V23.2854V23.2879V23.2905V23.293V23.2955V23.2981V23.3007V23.3033V23.3059V23.3085V23.3111V23.3138V23.3164V23.3191V23.3218V23.3244V23.3271V23.3299V23.3326V23.3353V23.3381V23.3408V23.3436V23.3464V23.3492V23.352V23.3548V23.3577V23.3605V23.3634V23.3663V23.3692V23.3721V23.375V23.3779V23.3809V23.3838V23.3868V23.3898V23.3927V23.3958V23.3988V23.4018V23.4049V23.4079V23.411V23.4141V23.4172V23.4203V23.4234V23.4266V23.4297V23.4329V23.4361V23.4393V23.4425V23.4457V23.4489V23.4522V23.4555V23.4587V23.462V23.4653V23.4687V23.472V23.4753V23.4787V23.4821V23.4855V23.4889V23.4923V23.4957V23.4992V23.5026V23.5061V23.5096V23.5131V23.5166V23.5201V23.5237V23.5273V23.5308V23.5344V23.538V23.5417V23.5453C9.14321 24.0976 9.59092 24.5453 10.1432 24.5453H17.0038C17.4537 24.5453 17.8482 24.2449 17.9678 23.8113L19.3197 18.9116C19.7604 18.3767 20.0043 17.7028 20.0043 17.0037V16.0036V12.0025V10.0022C20.0043 7.34942 18.9505 4.80534 17.0748 2.92957C15.199 1.0538 12.6549 0 10.0022 0C7.34943 0 4.80534 1.0538 2.92957 2.92957C1.0538 4.80534 0 7.34943 0 10.0022V12.0025V16.0036V17.0037C0 17.7995 0.316121 18.5627 0.87882 19.1254C1.44152 19.6881 2.2047 20.0042 3.00048 20.0042H4.00072C4.7965 20.0042 5.55968 19.6881 6.12238 19.1254C6.68508 18.5627 7.0012 17.7995 7.0012 17.0037V14.003C7.0012 13.2072 6.68508 12.444 6.12238 11.8813C5.55968 11.3186 4.7965 11.0025 4.00072 11.0025H2V10.0022C2 7.87986 2.84308 5.84448 4.34378 4.34378ZM2 13.0025V16.0036V17.0037C2 17.2691 2.10541 17.5235 2.29303 17.7112C2.48066 17.8988 2.73514 18.0042 3.00048 18.0042H4.00072C4.26606 18.0042 4.52054 17.8988 4.70817 17.7112C4.89579 17.5235 5.0012 17.2691 5.0012 17.0037V14.003C5.0012 13.7377 4.89579 13.4832 4.70817 13.2956C4.52054 13.1079 4.26606 13.0025 4.00072 13.0025H2ZM18.0043 13.0025H16.0036C15.7383 13.0025 15.4838 13.1079 15.2962 13.2956C15.1085 13.4832 15.0031 13.7377 15.0031 14.003V17.0037C15.0031 17.2691 15.1085 17.5235 15.2962 17.7112C15.4838 17.8988 15.7383 18.0042 16.0036 18.0042H17.0038C17.2692 18.0042 17.5237 17.8988 17.7113 17.7112C17.8989 17.5235 18.0043 17.2691 18.0043 17.0037V16.0036V13.0025Z"
		/>
	</svg>
);

const FunnelUpIcon = ({ className, title, ...props }: CustomIconProps) => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="100%"
		height="100%"
		viewBox="0 0 16 16"
		fill="none"
		stroke="currentColor"
		strokeLinecap="round"
		strokeLinejoin="round"
		className={cn("overflow-visible", className)}
		{...props}
	>
		{title ? <title>{title}</title> : null}
		<g transform="scale(0.5)" strokeWidth="2">
			<path d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 0 1-.659 1.591l-5.432 5.432a2.25 2.25 0 0 0-.659 1.591v2.927a2.25 2.25 0 0 1-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 0 0-.659-1.591L3.659 7.409A2.25 2.25 0 0 1 3 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0 1 12 3Z" />
		</g>
		<path strokeWidth="0.75" d="M12 13.9V7.5m-2.5 2.5l2.5-2.5 2.5 2.5" />
	</svg>
);

const FunnelDownIcon = ({ className, title, ...props }: CustomIconProps) => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="100%"
		height="100%"
		viewBox="0 0 16 16"
		fill="none"
		stroke="currentColor"
		strokeLinecap="round"
		strokeLinejoin="round"
		className={cn("overflow-visible", className)}
		{...props}
	>
		{title ? <title>{title}</title> : null}
		<g transform="scale(0.5)" strokeWidth="2">
			<path d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 0 1-.659 1.591l-5.432 5.432a2.25 2.25 0 0 0-.659 1.591v2.927a2.25 2.25 0 0 1-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 0 0-.659-1.591L3.659 7.409A2.25 2.25 0 0 1 3 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0 1 12 3Z" />
		</g>
		<path strokeWidth="0.75" d="M12 9.1v6.4m-2.5-2.5l2.5 2.5 2.5-2.5" />
	</svg>
);

const customIconMap = {
	"funnel-down": FunnelDownIcon,
	"funnel-up": FunnelUpIcon,
	headset: HeadsetIcon,
};

/**
 * Outline (default) glyphs. The resting registry — `Icon` falls back to these whenever a
 * `filled` variant is unavailable.
 */
const iconMap = {
	"archive-box": HiOutlineArchiveBox,
	"archive-box-arrow-down": HiOutlineArchiveBoxArrowDown,
	"arrow-down": HiOutlineArrowDown,
	"arrow-down-tray": HiOutlineArrowDownTray,
	"arrow-merge": RiGitMergeLine,
	"arrow-path": HiOutlineArrowPath,
	"arrows-pointing-in": HiOutlineArrowsPointingIn,
	"arrows-pointing-out": HiOutlineArrowsPointingOut,
	"arrow-right-end-on-rectangle": HiOutlineArrowRightEndOnRectangle,
	"arrow-small-down": HiOutlineArrowSmallDown,
	"arrow-small-up": HiOutlineArrowSmallUp,
	"arrow-top-right-on-square": HiOutlineArrowTopRightOnSquare,
	"arrow-up": HiOutlineArrowUp,
	"arrow-up-tray": HiOutlineArrowUpTray,
	"at-symbol": HiOutlineAtSymbol,
	bold: BiBold,
	bolt: HiOutlineBolt,
	"book-open": HiOutlineBookOpen,
	"building-office": HiOutlineBuildingOffice,
	"building-office-alt": HiOutlineBuildingOffice2,
	calendar: HiOutlineCalendar,
	"chat-bubble-oval-left": HiOutlineChatBubbleOvalLeft,
	check: HiOutlineCheck,
	"check-badge": HiOutlineCheckBadge,
	"check-circle": HiOutlineCheckCircle,
	"chevron-double-down": HiOutlineChevronDoubleDown,
	"chevron-double-left": HiOutlineChevronDoubleLeft,
	"chevron-double-right": HiOutlineChevronDoubleRight,
	"chevron-down": HiOutlineChevronDown,
	"chevron-left": HiOutlineChevronLeft,
	"chevron-right": HiOutlineChevronRight,
	"chevron-up": HiOutlineChevronUp,
	"chevron-up-down": HiOutlineChevronUpDown,
	clipboard: HiOutlineClipboard,
	clock: HiOutlineClock,
	"diamond-ring": RiDiamondRingLine,
	document: HiOutlineDocument,
	"document-arrow-down": HiOutlineDocumentArrowDown,
	"document-chart-bar": HiOutlineDocumentChartBar,
	"document-check": HiOutlineDocumentCheck,
	"document-plus": HiOutlineDocumentPlus,
	"document-text": HiOutlineDocumentText,
	"dots-vertical": HiOutlineDotsVertical,
	edit: AiOutlineEdit,
	"ellipsis-horizontal": HiOutlineEllipsisHorizontal,
	envelope: HiOutlineEnvelope,
	"exclamation-circle": HiOutlineExclamationCircle,
	"exclamation-triangle": HiOutlineExclamationTriangle,
	eye: HiOutlineEye,
	"eye-slash": HiOutlineEyeSlash,
	"folder-open": HiOutlineFolderOpen,
	formula: RiFormula,
	funnel: HiOutlineFunnel,
	"hand-raised": HiOutlineHandRaised,
	"hand-thumb-down": HiOutlineHandThumbDown,
	"hand-thumb-up": HiOutlineHandThumbUp,
	hashtag: HiOutlineHashtag,
	home: HiOutlineHome,
	hourglass: PiHourglassBold,
	"hourglass-low": PiHourglassLow,
	"id-card": HiOutlineIdentification,
	"information-circle": HiOutlineInformationCircle,
	italic: BiItalic,
	link: HiOutlineLink,
	"list-ordered": BiListOl,
	"list-unordered": BiListUl,
	"location-marker": HiOutlineLocationMarker,
	"lock-closed": HiOutlineLockClosed,
	"lock-closed-mini": HiMiniLockClosed,
	"lock-open": HiOutlineLockOpen,
	"lock-open-mini": HiMiniLockOpen,
	"magnifying-glass": HiOutlineMagnifyingGlass,
	mail: HiOutlineMail,
	"map-pin": HiOutlineMapPin,
	"minus-circle": HiOutlineMinusCircle,
	"plus-circle": HiOutlinePlusCircle,
	"paper-airplane": HiOutlinePaperAirplane,
	pencil: HiOutlinePencil,
	"pencil-square": HiOutlinePencilSquare,
	play: HiOutlinePlay,
	"play-circle": HiOutlinePlayCircle,
	plus: HiOutlinePlus,
	"presentation-chart-line": HiOutlinePresentationChartLine,
	"queue-list": HiOutlineQueueList,
	scale: HiOutlineScale,
	"shield-check": HiOutlineShieldCheck,
	"shield-slash": GoShieldSlash,
	slash: HiOutlineSlash,
	snowflake: FaRegSnowflake,
	sparkles: HiOutlineSparkles,
	"squares-2x2": HiOutlineSquares2X2,
	star: HiOutlineStar,
	stop: GoStop,
	tag: HiOutlineTag,
	"table-cells": HiOutlineTableCells,
	trash: HiOutlineTrash,
	underline: FaUnderline,
	user: HiOutlineUser,
	users: HiOutlineUsers,
	"user-check": FaUserCheck,
	"user-group": HiOutlineUserGroup,
	"user-plus": HiOutlineUserPlus,
	"view-columns": HiOutlineViewColumns,
	window: HiOutlineWindow,
	"x-circle": HiOutlineXCircle,
	"x-mark": HiOutlineXMark,
} satisfies Record<string, ReactIconType>;

/**
 * Solid glyphs, selected when `filled` is set. A subset of `iconMap`'s keys — `Icon` falls
 * back to the outline glyph for any key absent here.
 */
const filledIconMap: Partial<Record<keyof typeof iconMap, ReactIconType>> = {
	"archive-box": HiArchiveBox,
	"archive-box-arrow-down": HiArchiveBoxArrowDown,
	"arrow-down": HiArrowDown,
	"arrow-down-tray": HiArrowDownTray,
	"arrow-path": HiArrowPath,
	"arrows-pointing-in": HiArrowsPointingIn,
	"arrows-pointing-out": HiArrowsPointingOut,
	"arrow-right-end-on-rectangle": HiArrowRightEndOnRectangle,
	"arrow-small-down": HiArrowSmallDown,
	"arrow-small-up": HiArrowSmallUp,
	"arrow-top-right-on-square": HiArrowTopRightOnSquare,
	"arrow-up": HiArrowUp,
	"arrow-up-tray": HiArrowUpTray,
	"at-symbol": HiAtSymbol,
	bolt: HiBolt,
	"book-open": HiBookOpen,
	"building-office": HiBuildingOffice,
	"building-office-alt": HiBuildingOffice2,
	calendar: HiCalendar,
	"chat-bubble-oval-left": HiChatBubbleOvalLeft,
	check: HiCheck,
	"check-badge": HiCheckBadge,
	"check-circle": HiCheckCircle,
	"chevron-down": HiChevronDown,
	"chevron-double-down": HiChevronDoubleDown,
	"chevron-double-left": HiChevronDoubleLeft,
	"chevron-double-right": HiChevronDoubleRight,
	"chevron-left": HiChevronLeft,
	"chevron-right": HiChevronRight,
	"chevron-up": HiChevronUp,
	"chevron-up-down": HiChevronUpDown,
	clipboard: HiClipboard,
	clock: HiClock,
	"diamond-ring": RiDiamondRingFill,
	document: HiDocument,
	"document-arrow-down": HiDocumentArrowDown,
	"document-chart-bar": HiDocumentChartBar,
	"document-check": HiDocumentCheck,
	"document-plus": HiDocumentPlus,
	"document-text": HiDocumentText,
	"dots-vertical": HiDotsVertical,
	edit: AiFillEdit,
	"ellipsis-horizontal": HiEllipsisHorizontal,
	envelope: HiEnvelope,
	"exclamation-circle": HiExclamationCircle,
	"exclamation-triangle": HiExclamationTriangle,
	eye: HiEye,
	"eye-slash": HiEyeSlash,
	"folder-open": HiFolderOpen,
	formula: RiFormula,
	funnel: HiFunnel,
	"hand-raised": HiHandRaised,
	"hand-thumb-down": HiHandThumbDown,
	"hand-thumb-up": HiHandThumbUp,
	hashtag: HiHashtag,
	home: HiHome,
	hourglass: PiHourglassBold,
	"hourglass-low": PiHourglassLowFill,
	"id-card": HiIdentification,
	"information-circle": HiInformationCircle,
	link: HiLink,
	"location-marker": HiLocationMarker,
	"lock-closed": HiLockClosed,
	"lock-open": HiLockOpen,
	"magnifying-glass": HiMagnifyingGlass,
	mail: HiMail,
	"map-pin": HiMapPin,
	"minus-circle": HiMinusCircle,
	"plus-circle": HiPlusCircle,
	"paper-airplane": HiPaperAirplane,
	pencil: HiPencil,
	"pencil-square": HiPencilSquare,
	play: HiPlay,
	"play-circle": HiPlayCircle,
	plus: HiPlus,
	"presentation-chart-line": HiPresentationChartLine,
	"queue-list": HiQueueList,
	scale: HiScale,
	"shield-check": HiShieldCheck,
	slash: HiSlash,
	snowflake: FaSnowflake,
	sparkles: HiSparkles,
	"squares-2x2": HiSquares2X2,
	star: HiStar,
	tag: HiTag,
	"table-cells": HiTableCells,
	trash: HiTrash,
	user: HiUser,
	users: HiUsers,
	"user-group": HiUserGroup,
	"user-plus": HiUserPlus,
	"view-columns": HiViewColumns,
	window: HiWindow,
	"x-circle": HiXCircle,
	"x-mark": HiXMark,
};

type CustomIconType = keyof typeof customIconMap;
export type IconType = keyof typeof iconMap | CustomIconType;

const isCustomIcon = (type: IconType): type is CustomIconType => type in customIconMap;

/**
 * Semantic color → text token. The icons are monochrome and paint with `currentColor`, so
 * setting the text color tints both the fill (solid glyphs) and the stroke (outline glyphs).
 * Aligned with `Text`'s palette so icons and text tone from the same vocabulary. Omit to
 * inherit the surrounding color.
 */
const colorClasses = {
	brand: "text-brand-primary",
	accent: "text-brand-accent",
	primary: "text-primary",
	secondary: "text-secondary",
	tertiary: "text-tertiary",
	subtle: "text-subtle",
	inverse: "text-inverse",
	confirm: "text-confirm",
	warning: "text-intent-warning-text",
	danger: "text-danger",
};

/**
 * Named size → square dimension classes (large: 30px, medium: 24px, small: 20px, mini: 16px,
 * micro: 12px). Applied as `h-*`/`w-*` so they size both `react-icons` and custom SVGs
 * uniformly; non-square custom glyphs preserve their aspect ratio within the box.
 */
const sizeClasses = {
	large: "h-[30px] w-[30px]",
	medium: "h-[24px] w-[24px]",
	small: "h-[20px] w-[20px]",
	mini: "h-[16px] w-[16px]",
	micro: "h-[12px] w-[12px]",
};

const iconVariants = cva("shrink-0", {
	variants: {
		size: sizeClasses,
		color: colorClasses,
	},
	defaultVariants: {
		size: "medium",
	},
});

type IconVariantProps = VariantProps<typeof iconVariants>;

export type IconSize = NonNullable<IconVariantProps["size"]>;
export type IconColor = NonNullable<IconVariantProps["color"]>;

export const ICON_TYPES = [
	...Object.keys(iconMap),
	...Object.keys(customIconMap),
] as readonly IconType[];
export const ICON_SIZES = Object.keys(sizeClasses) as readonly IconSize[];
export const ICON_COLORS = Object.keys(colorClasses) as readonly IconColor[];

export type IconProps = Pick<SVGProps<SVGSVGElement>, "aria-label" | "aria-hidden" | "role"> & {
	/** Test selector hook, rendered as-is onto the icon's `<svg>` (or custom glyph). */
	"data-testid"?: string;

	/** Which glyph to render. */
	type: IconType;

	/**
	 * Size of the icon (large: 30px, medium: 24px, small: 20px, mini: 16px, micro: 12px).
	 * @default "medium"
	 */
	size?: IconVariantProps["size"];

	/**
	 * Semantic color, from the `Text`-aligned token map. Tints fill and stroke via
	 * `currentColor`. Omit to inherit the surrounding color.
	 */
	color?: IconVariantProps["color"];

	/** Render the solid glyph instead of the outline glyph (when a solid variant exists). */
	filled?: boolean;

	/** Additional classes merged onto the `<svg>`. Don't use for color or size. */
	className?: string;

	/** Tooltip text, rendered as the SVG's accessible `<title>`. */
	tooltip?: string;

	/** Analytics identifier. Expands to `data-pendo-id` + `data-amp-track-id`. */
	trackingId?: string;
};

/**
 * Icon primitive. Renders a `react-icons` glyph (or one of three custom SVGs) at a standard
 * size and semantic color. Owns no interaction — wrap it in `ClickableIcon` when you need a
 * clickable, hover-toned affordance.
 *
 * Sizes: (large: 30px, medium: 24px, small: 20px, mini: 16px, micro: 12px)
 *
 * Note: unlike most primitives this does not accept a `ref` prop — the rendered root is a
 * third-party icon component that does not accept a ref. Reach for `ClickableIcon` when you
 * need a focusable, ref-able element.
 */
export const Icon: FC<IconProps> = ({
	type,
	size,
	color,
	filled,
	className,
	tooltip,
	trackingId,
	...rest
}) => {
	const classes = cn(iconVariants({ size, color }), className);

	if (isCustomIcon(type)) {
		const CustomIcon = customIconMap[type];
		return (
			<CustomIcon className={classes} title={tooltip} {...trackingAttrs(trackingId)} {...rest} />
		);
	}

	const ReactIcon = filled ? (filledIconMap[type] ?? iconMap[type]) : iconMap[type];

	return <ReactIcon title={tooltip} className={classes} {...trackingAttrs(trackingId)} {...rest} />;
};
